import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const DEFAULT_LOCATION = { latitude: 48.8566, longitude: 2.3522, label: "Paris" };

const ALL_SHORTCUTS = [
  { to: "/planning", icon: "◷", title: "Planning", text: "Consulter les interventions" },
  { to: "/appels", icon: "☎", title: "Appels", text: "Accéder au suivi des appels" },
  { to: "/suivi", icon: "◎", title: "Suivi clients", text: "Ouvrir le suivi commercial" },
  { to: "/stock", icon: "□", title: "Stock", text: "Vérifier le matériel disponible" },
  { key: "backup", to: "/backup", icon: "□", title: "Backups", text: "Consulter les sauvegardes" },
  { key: "cdr", to: "/cdr", icon: "◉", title: "CDR", text: "Ouvrir le suivi CDR" },
  { key: "kanban", to: "/kanban", icon: "▦", title: "Kanban", text: "Consulter le suivi de production" },
  { key: "client", to: "/client", icon: "♙", title: "Clients", text: "Accéder à la base clients" },
  { key: "comcom", to: "/comcom", icon: "◴", title: "Heures", text: "Consulter le suivi des heures" },
  { key: "sms", to: "/sms", icon: "✉", title: "SMS", text: "Ouvrir la messagerie SMS" },
].map((shortcut) => ({ ...shortcut, key: shortcut.key || shortcut.to.slice(1) }));

const WIDGETS = [
  { key: "weather", icon: "☀", label: "Météo actuelle", description: "Température et conditions du jour" },
  { key: "forecast", icon: "◫", label: "Prévisions", description: "Météo des sept prochains jours" },
  { key: "planning", icon: "◷", label: "Mon planning", description: "Vos prochaines interventions" },
  { key: "calls", icon: "☎", label: "Mes appels", description: "Vos trois derniers appels actifs" },
  { key: "shortcuts", icon: "⌁", label: "Raccourcis", description: "Accès rapide aux applications" },
];

const DEFAULT_WIDGETS = WIDGETS.map((widget) => widget.key);
const DEFAULT_WIDGET_LAYOUT = WIDGETS.map((widget) => ({
  key: widget.key,
  size: widget.key === "shortcuts" ? "full" : "half",
}));
const DEFAULT_SHORTCUTS = ["planning", "appels", "suivi", "stock"];
const PREFS_URL = "https://cms.amservices.eu/api/preferences.php";
const HENRRI_REVENUE_URL = "https://cms.amservices.eu/api/henrri_revenue.php";

const WEATHER_LABELS = {
  0: ["Ciel dégagé", "☀️"], 1: ["Principalement dégagé", "🌤️"],
  2: ["Partiellement nuageux", "⛅"], 3: ["Couvert", "☁️"],
  45: ["Brouillard", "🌫️"], 48: ["Brouillard givrant", "🌫️"],
  51: ["Bruine légère", "🌦️"], 53: ["Bruine", "🌦️"], 55: ["Bruine forte", "🌧️"],
  61: ["Pluie légère", "🌦️"], 63: ["Pluie", "🌧️"], 65: ["Pluie forte", "🌧️"],
  71: ["Neige légère", "🌨️"], 73: ["Neige", "🌨️"], 75: ["Neige forte", "❄️"],
  80: ["Averses légères", "🌦️"], 81: ["Averses", "🌧️"], 82: ["Fortes averses", "⛈️"],
  95: ["Orage", "⛈️"], 96: ["Orage avec grêle", "⛈️"], 99: ["Fort orage avec grêle", "⛈️"],
};

function weatherInfo(code) {
  return WEATHER_LABELS[code] || ["Conditions variables", "🌤️"];
}

function getGreeting(hour) {
  if (hour < 12) return "Bonjour";
  if (hour < 18) return "Bon après-midi";
  return "Bonsoir";
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function formatAmount(value) {
  return Math.round(Number(value) || 0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function formatCurrency(value) {
  return `${formatAmount(value)} €`;
}

function CurrencyAmount({ value }) {
  return <>{formatAmount(value)} <span className="currency-symbol">€</span></>;
}

function revenueValue(record, key) {
  if (!record || typeof record !== "object") return 0;
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return Number(record[key] ?? record[pascalKey]) || 0;
}

function formatPlanningDay(dateString) {
  const date = new Date(`${dateString}T12:00:00`);
  const today = formatLocalDate(new Date());
  if (dateString === today) return "Aujourd’hui";
  return new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "numeric", month: "short" }).format(date);
}

const CALL_STATUS = {
  a_prendre: "À traiter",
  rappel: "À rappeler",
  en_cours: "En cours",
  resolu: "Résolu",
};

function Home({ user }) {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState(false);
  const [locationLabel, setLocationLabel] = useState(DEFAULT_LOCATION.label);
  const [planning, setPlanning] = useState([]);
  const [calls, setCalls] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [planningMessage, setPlanningMessage] = useState("");
  const [callsMessage, setCallsMessage] = useState("");
  const [visibleWidgets, setVisibleWidgets] = useState(DEFAULT_WIDGETS);
  const [widgetLayout, setWidgetLayout] = useState(DEFAULT_WIDGET_LAYOUT);
  const [selectedShortcuts, setSelectedShortcuts] = useState(DEFAULT_SHORTCUTS);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [selectedPlanningTask, setSelectedPlanningTask] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [savingWidgets, setSavingWidgets] = useState(false);
  const [henrriRevenue, setHenrriRevenue] = useState(null);
  const [henrriRevenueLoading, setHenrriRevenueLoading] = useState(false);
  const [henrriRevenueError, setHenrriRevenueError] = useState("");
  const [henrriRefreshKey, setHenrriRefreshKey] = useState(0);
  const userName = user?.nom || user?.name || user?.username || user?.login || "Utilisateur";
  const userLogin = user?.login || user?.username || "";
  const isAurelien = normalizeName(userLogin) === "aurelien";

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!isAurelien) {
      setHenrriRevenue(null);
      setHenrriRevenueError("");
      return undefined;
    }

    let cancelled = false;
    const loadRevenue = async () => {
      setHenrriRevenueLoading(true);
      setHenrriRevenueError("");
      try {
        const token = localStorage.getItem("am_token") || user?.token || "";
        if (!token) throw new Error("Session manquante. Reconnectez-vous.");
        const year = new Date().getFullYear();
        const response = await fetch(`${HENRRI_REVENUE_URL}?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Chiffre d'affaires indisponible.");
        }
        if (!cancelled) setHenrriRevenue(payload);
      } catch (error) {
        if (!cancelled) setHenrriRevenueError(error.message || "Chiffre d'affaires indisponible.");
      } finally {
        if (!cancelled) setHenrriRevenueLoading(false);
      }
    };

    loadRevenue();
    return () => { cancelled = true; };
  }, [isAurelien, user?.token, henrriRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    const loadWeather = async (location) => {
      try {
        const params = new URLSearchParams({
          latitude: String(location.latitude),
          longitude: String(location.longitude),
          current: "temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m",
          daily: "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max",
          timezone: "auto",
          forecast_days: "8",
        });
        const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
        if (!response.ok) throw new Error("Météo indisponible");
        const data = await response.json();
        if (!cancelled) {
          setWeather(data);
          setWeatherError(false);
          setLocationLabel(location.label);
        }
      } catch (error) {
        if (!cancelled) setWeatherError(true);
      }
    };
    const loadDefaultLocation = () => loadWeather(DEFAULT_LOCATION);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => loadWeather({
          latitude: coords.latitude,
          longitude: coords.longitude,
          label: "Votre position",
        }),
        loadDefaultLocation,
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 30 * 60 * 1000 }
      );
    } else {
      loadDefaultLocation();
    }

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPlanning = async () => {
      try {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + 6);
        const [peopleResponse, tasksResponse] = await Promise.all([
          fetch("https://planning.amservices.eu/api/personnes/get.php"),
          fetch(`https://planning.amservices.eu/api/planning/get.php?start=${formatLocalDate(startDate)}&end=${formatLocalDate(endDate)}`),
        ]);
        if (!peopleResponse.ok || !tasksResponse.ok) throw new Error("Planning indisponible");

        const [people, tasks] = await Promise.all([peopleResponse.json(), tasksResponse.json()]);
        const identities = [user?.nom, user?.name, user?.username, user?.login]
          .map(normalizeName)
          .filter(Boolean);
        const person = Array.isArray(people)
          ? people.find((item) => String(item.id) === String(user?.personne_id || ""))
            || people.find((item) => identities.includes(normalizeName(item.nom)))
          : null;

        if (!person) {
          if (!cancelled) setPlanningMessage("Votre profil n’est pas encore associé au planning.");
          return;
        }

        const personalTasks = (Array.isArray(tasks) ? tasks : [])
          .filter((task) => String(task.personne_id) === String(person.id))
          .sort((a, b) => `${a.date} ${a.heure_debut || ""}`.localeCompare(`${b.date} ${b.heure_debut || ""}`))
          .slice(0, 4);

        if (!cancelled) {
          setPlanning(personalTasks);
          setPlanningMessage(personalTasks.length ? "" : "Aucune intervention prévue dans les sept prochains jours.");
        }
      } catch (error) {
        if (!cancelled) setPlanningMessage("Impossible de charger votre planning pour le moment.");
      }
    };

    const loadCalls = async () => {
      try {
        const login = user?.login || user?.username;
        if (!login) throw new Error("Identifiant manquant");

        const loginResponse = await fetch(
          `https://appel.amservices.eu/api/auto_login.php?token=${encodeURIComponent(login)}`
        );
        const loginData = await loginResponse.json();
        if (!loginResponse.ok || !loginData.success || !loginData.token) {
          throw new Error("Connexion aux appels impossible");
        }

        const types = ["depannage"];
        if (["bureau", "admin"].includes(loginData.user?.role)) types.push("bureau");

        const responses = await Promise.all(types.map((type) =>
          fetch(`https://appel.amservices.eu/api/get_appels.php?type=${type}`, {
            headers: { Authorization: loginData.token },
          })
        ));
        if (responses.some((response) => !response.ok)) throw new Error("Appels indisponibles");

        const payloads = await Promise.all(responses.map((response) => response.json()));
        const callUserId = loginData.user?.id;
        const callUserName = normalizeName(loginData.user?.name);
        const personalCalls = payloads
          .flatMap((payload) => Array.isArray(payload.data) ? payload.data : [])
          .filter((call) =>
            String(call.affecte_a) === String(callUserId)
            || normalizeName(call.affecte_nom) === callUserName
            || normalizeName(call.pris_par) === callUserName
          )
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 3);

        if (!cancelled) {
          setCalls(personalCalls);
          setCallsMessage(personalCalls.length ? "" : "Aucun appel actif ne vous est affecté.");
        }
      } catch (error) {
        if (!cancelled) setCallsMessage("Impossible de charger vos appels pour le moment.");
      }
    };

    Promise.allSettled([loadPlanning(), loadCalls()]).finally(() => {
      if (!cancelled) setActivityLoading(false);
    });

    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!userLogin) return;
    let cancelled = false;

    fetch(`${PREFS_URL}?login=${encodeURIComponent(userLogin)}`)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled || !data.success) return;
        if (Array.isArray(data.prefs?.homeWidgets)) {
          setVisibleWidgets(data.prefs.homeWidgets.filter((key) => DEFAULT_WIDGETS.includes(key)));
        }
        if (Array.isArray(data.prefs?.homeWidgetLayout)) {
          const savedLayout = data.prefs.homeWidgetLayout
            .filter((item) => item && DEFAULT_WIDGETS.includes(item.key))
            .map((item) => ({ key: item.key, size: item.size === "full" ? "full" : "half" }));
          const missingWidgets = DEFAULT_WIDGET_LAYOUT.filter(
            (item) => !savedLayout.some((saved) => saved.key === item.key)
          );
          setWidgetLayout([...savedLayout, ...missingWidgets]);
        }
        if (Array.isArray(data.prefs?.homeShortcuts)) {
          setSelectedShortcuts(
            data.prefs.homeShortcuts.filter((key) =>
              ALL_SHORTCUTS.some((shortcut) => shortcut.key === key)
            )
          );
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [userLogin]);

  useEffect(() => {
    if (!showWidgetSettings) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowWidgetSettings(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showWidgetSettings]);

  useEffect(() => {
    if (!selectedPlanningTask) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedPlanningTask(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedPlanningTask]);

  useEffect(() => {
    if (!selectedCall) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedCall(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedCall]);

  const savePreferences = async (preferences) => {
    if (!userLogin) return;
    setSavingWidgets(true);
    try {
      await fetch(PREFS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: userLogin, prefs: preferences }),
      });
    } catch (error) {
      // Les choix restent appliqués visuellement si le réseau est indisponible.
    } finally {
      setSavingWidgets(false);
    }
  };

  const toggleWidget = (widgetKey) => {
    const nextWidgets = visibleWidgets.includes(widgetKey)
      ? visibleWidgets.filter((key) => key !== widgetKey)
      : [...visibleWidgets, widgetKey];
    setVisibleWidgets(nextWidgets);
    savePreferences({ homeWidgets: nextWidgets });
  };

  const setWidgetSize = (widgetKey, size) => {
    const nextLayout = widgetLayout.map((item) =>
      item.key === widgetKey ? { ...item, size } : item
    );
    setWidgetLayout(nextLayout);
    savePreferences({ homeWidgetLayout: nextLayout });
  };

  const moveWidget = (targetKey) => {
    if (!draggedWidget || draggedWidget === targetKey) return;
    const nextLayout = [...widgetLayout];
    const fromIndex = nextLayout.findIndex((item) => item.key === draggedWidget);
    const toIndex = nextLayout.findIndex((item) => item.key === targetKey);
    if (fromIndex < 0 || toIndex < 0) return;
    const [moved] = nextLayout.splice(fromIndex, 1);
    nextLayout.splice(toIndex, 0, moved);
    setWidgetLayout(nextLayout);
    setDraggedWidget(null);
    savePreferences({ homeWidgetLayout: nextLayout });
  };

  const toggleShortcut = (shortcutKey) => {
    const nextShortcuts = selectedShortcuts.includes(shortcutKey)
      ? selectedShortcuts.filter((key) => key !== shortcutKey)
      : [...selectedShortcuts, shortcutKey];
    setSelectedShortcuts(nextShortcuts);
    savePreferences({ homeShortcuts: nextShortcuts });
  };

  const widgetIsVisible = (widgetKey) => visibleWidgets.includes(widgetKey);
  const displayedShortcuts = ALL_SHORTCUTS.filter((shortcut) =>
    selectedShortcuts.includes(shortcut.key)
  );

  const formattedDate = useMemo(() => new Intl.DateTimeFormat("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(now), [now]);

  const currentWeather = weather?.current;
  const [condition, weatherIcon] = weatherInfo(currentWeather?.weather_code);
  const yearlyServices = revenueValue(henrriRevenue?.yearly, "totalServicesRevenue");
  const yearlyProducts = revenueValue(henrriRevenue?.yearly, "totalProductsRevenue");
  const yearlyHours = revenueValue(henrriRevenue?.yearly, "totalServiceHours");
  const yearlyTotal = yearlyServices + yearlyProducts;
  const monthlyRevenue = (henrriRevenue?.months || [])
    .map((month) => ({
      month: revenueValue(month, "month"),
      total: revenueValue(month, "totalServicesRevenue") + revenueValue(month, "totalProductsRevenue"),
    }))
    .filter((month) => month.month >= 1 && month.month <= 12)
    .sort((a, b) => a.month - b.month);
  const maxMonthlyRevenue = Math.max(1, ...monthlyRevenue.map((month) => month.total));

  const renderWidget = (widgetKey) => {
    if (widgetKey === "weather") {
      return (
        <section className="weather-card glass-panel">
          <div className="panel-heading">
            <div><span className="panel-kicker">Météo aujourd’hui</span><h2>{locationLabel}</h2></div>
            <span className="weather-icon" aria-hidden="true">{currentWeather ? weatherIcon : "…"}</span>
          </div>
          {!currentWeather && !weatherError && <div className="weather-loading">Chargement de la météo…</div>}
          {weatherError && <div className="weather-loading">La météo n’est pas disponible pour le moment.</div>}
          {currentWeather && (
            <>
              <div className="weather-current">
                <div className="weather-temperature">{Math.round(currentWeather.temperature_2m)}°</div>
                <div><strong>{condition}</strong><span>Ressenti {Math.round(currentWeather.apparent_temperature)}°</span></div>
              </div>
              <div className="weather-details">
                <div><span>Humidité</span><strong>{currentWeather.relative_humidity_2m}%</strong></div>
                <div><span>Vent</span><strong>{Math.round(currentWeather.wind_speed_10m)} km/h</strong></div>
                <div><span>Pluie</span><strong>{weather.daily?.precipitation_probability_max?.[0] ?? 0}%</strong></div>
              </div>
            </>
          )}
        </section>
      );
    }

    if (widgetKey === "forecast") {
      return (
        <section className="forecast-card glass-panel">
          <div className="panel-heading">
            <div><span className="panel-kicker">Prévisions</span><h2>Les sept prochains jours</h2></div>
          </div>
          <div className="forecast-list">
            {weather?.daily?.time?.slice(1, 8).map((day, index) => {
              const dataIndex = index + 1;
              const [, icon] = weatherInfo(weather.daily.weather_code[dataIndex]);
              return (
                <div className="forecast-day" key={day}>
                  <span>{new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(new Date(`${day}T12:00:00`))}</span>
                  <span className="forecast-icon">{icon}</span>
                  <strong>{Math.round(weather.daily.temperature_2m_max[dataIndex])}°<small>{Math.round(weather.daily.temperature_2m_min[dataIndex])}°</small></strong>
                </div>
              );
            })}
            {!weather && <div className="weather-loading">Prévisions en cours de chargement…</div>}
          </div>
        </section>
      );
    }

    if (widgetKey === "planning") {
      return (
        <section className="activity-card glass-panel">
          <div className="panel-heading activity-heading">
            <div><span className="panel-kicker">Mon planning</span><h2>Les prochains rendez-vous</h2></div>
            <Link to="/planning" className="activity-link">Tout voir →</Link>
          </div>
          <div className="activity-list">
            {planning.map((task) => (
              <button
                type="button"
                className="activity-row activity-row-button"
                onClick={() => setSelectedPlanningTask(task)}
                key={task.id}
              >
                <div className="activity-date">
                  <strong>{formatPlanningDay(task.date)}</strong>
                  <span>{String(task.heure_debut || "").slice(0, 5)}{task.heure_fin ? ` – ${String(task.heure_fin).slice(0, 5)}` : ""}</span>
                </div>
                <div className="activity-main">
                  <strong>{task.titre || "Intervention"}</strong>
                  <span>{task.adresse || task.description || "Aucun détail renseigné"}</span>
                </div>
                <span className="activity-arrow">→</span>
              </button>
            ))}
            {activityLoading && !planning.length && <div className="activity-empty">Chargement du planning…</div>}
            {!activityLoading && !planning.length && <div className="activity-empty">{planningMessage}</div>}
          </div>
        </section>
      );
    }

    if (widgetKey === "calls") {
      return (
        <section className="activity-card glass-panel">
          <div className="panel-heading activity-heading">
            <div><span className="panel-kicker">Mes appels</span><h2>Les trois derniers appels</h2></div>
            <Link to="/appels" className="activity-link">Tout voir →</Link>
          </div>
          <div className="activity-list">
            {calls.map((call) => (
              <button
                type="button"
                className="activity-row activity-row-button call-row"
                onClick={() => setSelectedCall(call)}
                key={`${call.type}-${call.id}`}
              >
                <span className={`call-dot ${call.urgence === "urgent" ? "urgent" : ""}`} />
                <div className="activity-main">
                  <strong>{call.client || "Client non renseigné"}</strong>
                  <span>{call.tel || call.adresse || "Aucune coordonnée"}</span>
                </div>
                <div className="call-meta">
                  <strong>{CALL_STATUS[call.statut] || call.statut}</strong>
                  <span>{call.created_at ? new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(new Date(call.created_at)) : ""}</span>
                </div>
              </button>
            ))}
            {activityLoading && !calls.length && <div className="activity-empty">Chargement des appels…</div>}
            {!activityLoading && !calls.length && <div className="activity-empty">{callsMessage}</div>}
          </div>
        </section>
      );
    }

    if (widgetKey === "shortcuts") {
      return (
        <section className="shortcuts-widget glass-panel">
          <div className="section-title">
            <span className="panel-kicker">Accès direct</span><h2>Vos outils du quotidien</h2>
          </div>
          {displayedShortcuts.length ? (
            <div className="quick-grid">
              {displayedShortcuts.map((link) => (
                <Link className="quick-card" to={link.to} key={link.key}>
                  <span className="quick-icon" aria-hidden="true">{link.icon}</span>
                  <div><h3>{link.title}</h3><p>{link.text}</p></div>
                  <span className="quick-arrow" aria-hidden="true">→</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="shortcuts-empty">Ajoutez vos applications préférées depuis « Personnaliser ».</div>
          )}
        </section>
      );
    }

    return null;
  };

  return (
    <main className="home-page">
      <div className="home-orb home-orb-one" />
      <div className="home-orb home-orb-two" />
      <section className="home-content">
        <aside className="home-update-banner" aria-label="Nouvelle mise à jour">
          <div className="home-update-icon" aria-hidden="true">✦</div>
          <div className="home-update-copy">
            <div className="home-update-heading">
              <span className="home-update-badge">Nouveau</span>
              <strong>Nouvelle mise à jour de l’espace Clients</strong>
            </div>
            <p>Les clients Henrri, leurs devis et les interventions du planning sont maintenant réunis dans un seul dossier.</p>
          </div>
          <Link className="home-update-link" to="/client">
            Découvrir <span aria-hidden="true">→</span>
          </Link>
        </aside>

        <header className="home-hero">
          <div>
            <p className="home-eyebrow">{formattedDate}</p>
            <h1>{getGreeting(now.getHours())}, {userName}</h1>
            <p className="home-subtitle">
              Retrouvez l’essentiel de votre journée et accédez rapidement à vos outils.
            </p>
          </div>
          <div className="home-actions">
            <div className="home-clock">
              {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </div>
            <a
              className="android-download-button"
              href="https://media.amservices.eu/CMS.apk"
              title="Télécharger le pack de widgets pour Android"
            >
              <span aria-hidden="true">↓</span>
              Widgets Android
              <small>APK · dernière version</small>
            </a>
            <button className="customize-button" type="button" onClick={() => setShowWidgetSettings(true)}>
              <span aria-hidden="true">⚙</span> Personnaliser
            </button>
          </div>
        </header>

        {isAurelien && (
          <section className="henrri-revenue-card glass-panel" aria-live="polite">
            <div className="henrri-revenue-heading">
              <div>
                <span className="panel-kicker">Henrri · chiffre d'affaires</span>
                <h2>Activité {henrriRevenue?.year || new Date().getFullYear()}</h2>
              </div>
              <button
                type="button"
                className="henrri-refresh-button"
                onClick={() => setHenrriRefreshKey((value) => value + 1)}
                disabled={henrriRevenueLoading}
              >
                {henrriRevenueLoading ? "Actualisation…" : "Actualiser"}
              </button>
            </div>

            {henrriRevenueLoading && !henrriRevenue && (
              <div className="henrri-revenue-state">Chargement du chiffre d'affaires Henrri…</div>
            )}
            {henrriRevenueError && !henrriRevenue && (
              <div className="henrri-revenue-state henrri-revenue-error">{henrriRevenueError}</div>
            )}
            {henrriRevenue && (
              <div className="henrri-revenue-content">
                <div className="henrri-revenue-summary">
                  <div className="henrri-revenue-total">
                    <span>CA total</span>
                    <strong><CurrencyAmount value={yearlyTotal} /></strong>
                  </div>
                  <div className="henrri-revenue-metrics">
                    <div><span>Services</span><strong><CurrencyAmount value={yearlyServices} /></strong></div>
                    <div><span>Produits</span><strong><CurrencyAmount value={yearlyProducts} /></strong></div>
                    <div><span>Heures facturées</span><strong>{yearlyHours.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h</strong></div>
                  </div>
                </div>
                <div className="henrri-monthly-chart" aria-label="Chiffre d'affaires mensuel">
                  {monthlyRevenue.map((item) => (
                    <div className="henrri-month" key={item.month} title={formatCurrency(item.total)}>
                      <div className="henrri-month-track">
                        <span style={{ height: `${Math.max(3, (item.total / maxMonthlyRevenue) * 100)}%` }} />
                      </div>
                      <strong>{new Intl.DateTimeFormat("fr-FR", { month: "short" }).format(new Date(2020, item.month - 1, 1)).replace(".", "")}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {henrriRevenueError && henrriRevenue && (
              <div className="henrri-revenue-warning">Dernières données affichées · {henrriRevenueError}</div>
            )}
          </section>
        )}

        <div className="home-dashboard-grid">
          {widgetLayout
            .filter((item) => widgetIsVisible(item.key))
            .map((item) => (
              <div className={`dashboard-widget widget-${item.size}`} key={item.key}>
                {renderWidget(item.key)}
              </div>
            ))}
        </div>

        {false && (
        <div className={`home-grid ${widgetIsVisible("weather") !== widgetIsVisible("forecast") ? "single-widget" : ""}`}>
          {widgetIsVisible("weather") && <section className="weather-card glass-panel">
            <div className="panel-heading">
              <div><span className="panel-kicker">Météo aujourd’hui</span><h2>{locationLabel}</h2></div>
              <span className="weather-icon" aria-hidden="true">{currentWeather ? weatherIcon : "…"}</span>
            </div>
            {!currentWeather && !weatherError && <div className="weather-loading">Chargement de la météo…</div>}
            {weatherError && <div className="weather-loading">La météo n’est pas disponible pour le moment.</div>}
            {currentWeather && (
              <>
                <div className="weather-current">
                  <div className="weather-temperature">{Math.round(currentWeather.temperature_2m)}°</div>
                  <div><strong>{condition}</strong><span>Ressenti {Math.round(currentWeather.apparent_temperature)}°</span></div>
                </div>
                <div className="weather-details">
                  <div><span>Humidité</span><strong>{currentWeather.relative_humidity_2m}%</strong></div>
                  <div><span>Vent</span><strong>{Math.round(currentWeather.wind_speed_10m)} km/h</strong></div>
                  <div><span>Pluie</span><strong>{weather.daily?.precipitation_probability_max?.[0] ?? 0}%</strong></div>
                </div>
              </>
            )}
          </section>}

          {widgetIsVisible("forecast") && <section className="forecast-card glass-panel">
            <div className="panel-heading">
              <div><span className="panel-kicker">Prévisions</span><h2>Les prochains jours</h2></div>
            </div>
            <div className="forecast-list">
              {weather?.daily?.time?.slice(1, 8).map((day, index) => {
                const dataIndex = index + 1;
                const [, icon] = weatherInfo(weather.daily.weather_code[dataIndex]);
                return (
                  <div className="forecast-day" key={day}>
                    <span>{new Intl.DateTimeFormat("fr-FR", { weekday: "short" }).format(new Date(`${day}T12:00:00`))}</span>
                    <span className="forecast-icon">{icon}</span>
                    <strong>{Math.round(weather.daily.temperature_2m_max[dataIndex])}°<small>{Math.round(weather.daily.temperature_2m_min[dataIndex])}°</small></strong>
                  </div>
                );
              })}
              {!weather && <div className="weather-loading">Prévisions en cours de chargement…</div>}
            </div>
          </section>}
        </div>
        )}

        {false && (
        <section className="quick-section">
          {(widgetIsVisible("planning") || widgetIsVisible("calls")) && (
          <div className="home-activity-grid">
            {widgetIsVisible("planning") && <section className="activity-card glass-panel">
              <div className="panel-heading activity-heading">
                <div><span className="panel-kicker">Mon planning</span><h2>Les prochains rendez-vous</h2></div>
                <Link to="/planning" className="activity-link">Tout voir →</Link>
              </div>
              <div className="activity-list">
                {planning.map((task) => (
                  <button
                    type="button"
                    className="activity-row activity-row-button"
                    onClick={() => setSelectedPlanningTask(task)}
                    key={task.id}
                  >
                    <div className="activity-date">
                      <strong>{formatPlanningDay(task.date)}</strong>
                      <span>{String(task.heure_debut || "").slice(0, 5)}{task.heure_fin ? ` – ${String(task.heure_fin).slice(0, 5)}` : ""}</span>
                    </div>
                    <div className="activity-main">
                      <strong>{task.titre || "Intervention"}</strong>
                      <span>{task.adresse || task.description || "Aucun détail renseigné"}</span>
                    </div>
                    <span className="activity-arrow">→</span>
                  </button>
                ))}
                {activityLoading && !planning.length && <div className="activity-empty">Chargement du planning…</div>}
                {!activityLoading && !planning.length && <div className="activity-empty">{planningMessage}</div>}
              </div>
            </section>}

            {widgetIsVisible("calls") && <section className="activity-card glass-panel">
              <div className="panel-heading activity-heading">
                <div><span className="panel-kicker">Mes appels</span><h2>Les trois derniers appels</h2></div>
                <Link to="/appels" className="activity-link">Tout voir →</Link>
              </div>
              <div className="activity-list">
                {calls.map((call) => (
                  <button
                    type="button"
                    className="activity-row activity-row-button call-row"
                    onClick={() => setSelectedCall(call)}
                    key={`${call.type}-${call.id}`}
                  >
                    <span className={`call-dot ${call.urgence === "urgent" ? "urgent" : ""}`} />
                    <div className="activity-main">
                      <strong>{call.client || "Client non renseigné"}</strong>
                      <span>{call.tel || call.adresse || "Aucune coordonnée"}</span>
                    </div>
                    <div className="call-meta">
                      <strong>{CALL_STATUS[call.statut] || call.statut}</strong>
                      <span>{call.created_at ? new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(new Date(call.created_at)) : ""}</span>
                    </div>
                  </button>
                ))}
                {activityLoading && !calls.length && <div className="activity-empty">Chargement des appels…</div>}
                {!activityLoading && !calls.length && <div className="activity-empty">{callsMessage}</div>}
              </div>
            </section>}
          </div>
          )}

          {widgetIsVisible("shortcuts") && <>
          <div className="section-title">
            <span className="panel-kicker">Accès direct</span><h2>Vos outils du quotidien</h2>
          </div>
          <div className="quick-grid">
            {ALL_SHORTCUTS.map((link) => (
              <Link className="quick-card glass-panel" to={link.to} key={link.to}>
                <span className="quick-icon" aria-hidden="true">{link.icon}</span>
                <div><h3>{link.title}</h3><p>{link.text}</p></div>
                <span className="quick-arrow" aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
          </>}
        </section>
        )}
        {(widgetIsVisible("weather") || widgetIsVisible("forecast")) && (
          <footer className="home-footer">Données météo fournies par Open-Meteo</footer>
        )}
      </section>

      {showWidgetSettings && (
        <div className="widget-settings-overlay" onMouseDown={() => setShowWidgetSettings(false)}>
          <section
            className="widget-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="widget-settings-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="widget-settings-header">
              <div>
                <span className="panel-kicker">Mon tableau de bord</span>
                <h2 id="widget-settings-title">Choisir mes widgets</h2>
              </div>
              <button type="button" onClick={() => setShowWidgetSettings(false)} aria-label="Fermer">×</button>
            </div>
            <p className="widget-settings-help">
              Glissez les widgets pour changer leur ordre, puis choisissez leur taille et leur visibilité.
            </p>
            <div className="widget-options">
              {widgetLayout.map((layoutItem) => {
                const widget = WIDGETS.find((item) => item.key === layoutItem.key);
                if (!widget) return null;
                const isActive = widgetIsVisible(widget.key);
                return (
                  <div
                    className={`widget-option ${isActive ? "active" : ""} ${draggedWidget === widget.key ? "dragging" : ""}`}
                    draggable
                    onDragStart={() => setDraggedWidget(widget.key)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => moveWidget(widget.key)}
                    onDragEnd={() => setDraggedWidget(null)}
                    key={widget.key}
                  >
                    <span className="widget-drag-handle" title="Déplacer" aria-hidden="true">⠿</span>
                    <button
                      type="button"
                      className="widget-visibility-button"
                      onClick={() => toggleWidget(widget.key)}
                      aria-pressed={isActive}
                    >
                      <span className="widget-option-icon" aria-hidden="true">{widget.icon}</span>
                      <span className="widget-option-copy">
                        <strong>{widget.label}</strong>
                        <small>{widget.description}</small>
                      </span>
                      <span className="widget-switch" aria-hidden="true"><span /></span>
                    </button>
                    <div className="widget-size-buttons" aria-label={`Taille du widget ${widget.label}`}>
                      <button
                        type="button"
                        className={layoutItem.size === "half" ? "active" : ""}
                        onClick={() => setWidgetSize(widget.key, "half")}
                        title="Demi-largeur"
                      >
                        ½
                      </button>
                      <button
                        type="button"
                        className={layoutItem.size === "full" ? "active" : ""}
                        onClick={() => setWidgetSize(widget.key, "full")}
                        title="Pleine largeur"
                      >
                        1/1
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="shortcut-settings">
              <div className="shortcut-settings-title">
                <span className="panel-kicker">Raccourcis</span>
                <h3>Choisir mes applications</h3>
              </div>
              <div className="shortcut-options">
                {ALL_SHORTCUTS.map((shortcut) => {
                  const isSelected = selectedShortcuts.includes(shortcut.key);
                  return (
                    <button
                      type="button"
                      className={isSelected ? "active" : ""}
                      onClick={() => toggleShortcut(shortcut.key)}
                      aria-pressed={isSelected}
                      key={shortcut.key}
                    >
                      <span aria-hidden="true">{shortcut.icon}</span>
                      {shortcut.title}
                      <strong aria-hidden="true">{isSelected ? "✓" : "+"}</strong>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="widget-settings-footer">
              <span>{savingWidgets ? "Sauvegarde…" : "Vos choix sont enregistrés automatiquement."}</span>
              <button type="button" onClick={() => setShowWidgetSettings(false)}>Terminer</button>
            </div>
          </section>
        </div>
      )}

      {selectedPlanningTask && (
        <div className="planning-detail-overlay" onMouseDown={() => setSelectedPlanningTask(null)}>
          <section
            className="planning-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="planning-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="planning-detail-header">
              <div>
                <span className="panel-kicker">Détail du rendez-vous</span>
                <h2 id="planning-detail-title">{selectedPlanningTask.titre || "Intervention"}</h2>
              </div>
              <button type="button" onClick={() => setSelectedPlanningTask(null)} aria-label="Fermer">×</button>
            </div>

            <div className="planning-detail-schedule">
              <span className="planning-detail-calendar" aria-hidden="true">◷</span>
              <div>
                <strong>
                  {new Intl.DateTimeFormat("fr-FR", {
                    weekday: "long", day: "numeric", month: "long", year: "numeric",
                  }).format(new Date(`${selectedPlanningTask.date}T12:00:00`))}
                </strong>
                <span>
                  {String(selectedPlanningTask.heure_debut || "").slice(0, 5)}
                  {selectedPlanningTask.heure_fin
                    ? ` – ${String(selectedPlanningTask.heure_fin).slice(0, 5)}`
                    : ""}
                </span>
              </div>
            </div>

            <dl className="planning-detail-list">
              <div>
                <dt>Adresse</dt>
                <dd>{selectedPlanningTask.adresse || "Aucune adresse renseignée"}</dd>
              </div>
              <div>
                <dt>Description</dt>
                <dd>{selectedPlanningTask.description || "Aucune description renseignée"}</dd>
              </div>
              {selectedPlanningTask.created_by && (
                <div>
                  <dt>Créé par</dt>
                  <dd>{selectedPlanningTask.created_by}</dd>
                </div>
              )}
              {selectedPlanningTask.updated_by && (
                <div>
                  <dt>Dernière modification</dt>
                  <dd>{selectedPlanningTask.updated_by}</dd>
                </div>
              )}
            </dl>

            <div className="planning-detail-footer">
              <button type="button" onClick={() => setSelectedPlanningTask(null)}>Fermer</button>
              <Link to="/planning">Ouvrir le planning complet →</Link>
            </div>
          </section>
        </div>
      )}

      {selectedCall && (
        <div className="planning-detail-overlay" onMouseDown={() => setSelectedCall(null)}>
          <section
            className="planning-detail-modal call-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="call-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="planning-detail-header">
              <div>
                <span className="panel-kicker">Détail de l’appel</span>
                <h2 id="call-detail-title">{selectedCall.client || "Client non renseigné"}</h2>
              </div>
              <button type="button" onClick={() => setSelectedCall(null)} aria-label="Fermer">×</button>
            </div>

            <div className="planning-detail-schedule call-detail-summary">
              <span className="planning-detail-calendar" aria-hidden="true">☎</span>
              <div>
                <strong>{CALL_STATUS[selectedCall.statut] || selectedCall.statut || "À traiter"}</strong>
                <span>
                  {selectedCall.created_at
                    ? new Intl.DateTimeFormat("fr-FR", {
                        weekday: "long", day: "numeric", month: "long", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      }).format(new Date(String(selectedCall.created_at).replace(" ", "T")))
                    : "Date non renseignée"}
                </span>
              </div>
              <span className={`call-detail-urgency ${selectedCall.urgence === "urgent" ? "urgent" : ""}`}>
                {selectedCall.urgence === "urgent" ? "Urgent" : "Normal"}
              </span>
            </div>

            <dl className="planning-detail-list">
              <div>
                <dt>Téléphone</dt>
                <dd>{selectedCall.tel || "Aucun numéro renseigné"}</dd>
              </div>
              <div>
                <dt>Adresse</dt>
                <dd>{selectedCall.adresse || "Aucune adresse renseignée"}</dd>
              </div>
              <div>
                <dt>Note</dt>
                <dd>{selectedCall.note || "Aucune note renseignée"}</dd>
              </div>
              <div>
                <dt>Affecté à</dt>
                <dd>{selectedCall.affecte_nom || "Non affecté"}</dd>
              </div>
              {selectedCall.pris_par && (
                <div>
                  <dt>Pris en charge par</dt>
                  <dd>{selectedCall.pris_par}</dd>
                </div>
              )}
              {selectedCall.created_par && (
                <div>
                  <dt>Créé par</dt>
                  <dd>{selectedCall.created_par}</dd>
                </div>
              )}
            </dl>

            <div className="planning-detail-footer call-detail-footer">
              <button type="button" onClick={() => setSelectedCall(null)}>Fermer</button>
              {selectedCall.tel && (
                <a className="call-phone-button" href={`tel:${String(selectedCall.tel).replace(/[^\d+]/g, "")}`}>
                  Appeler
                </a>
              )}
              <Link to="/appels">Ouvrir le suivi complet →</Link>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default Home;
